/**
 * Sign-in state. The session itself is an HttpOnly cookie issued by the
 * Universe server (server/), whether it came from Keyra, a Ciright
 * username/password, or signup. The browser never holds a token; it keeps only
 * a display copy of the profile so the header doesn't flash on reload, and
 * `refreshSession()` asks the server what is actually true.
 */
import { API_BASE } from './config';

const AUTH_PATH = '/api/auth';

export const CIRIGHT_AUTH = {
  subscriptionId: String(import.meta.env.VITE_CIRIGHT_SUBSCRIPTION_ID || '9329'),
  verticalId: String(import.meta.env.VITE_CIRIGHT_VERTICAL_ID || '18'),
  appId: String(import.meta.env.VITE_CIRIGHT_APP_ID || '6834'),
} as const;

export type AuthMethod = 'keyra' | 'ciright' | 'dev';

export interface CirightProfile {
  uid: string;
  name: string;
  email: string | null;
  userName: string | null;
  title: string | null;
  customerName: string | null;
  photo: string | null;
}

export interface AuthSession {
  method: AuthMethod;
  profile: CirightProfile;
  canUseStudio: boolean;
}

export interface AuthStatus {
  keyra: { configured: boolean; clientId: string | null; authOrigin: string; redirectUri: string | null };
  registration: { configured: boolean };
  devBypass: boolean;
}

/** Changes whenever the signed-in identity does, for cache keys. */
export function sessionKey(s: AuthSession | null): string {
  return s ? `${s.method}:${s.profile.uid}` : '';
}

export class AuthError extends Error {
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(message: string, code: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.fields = fields;
  }
}

/* ---------- session store ---------- */

const PROFILE_KEY = 'universe.session.v1';
const REMEMBER_KEY = 'auth_remember_me';
/** Pre-server builds kept a Ciright token in storage; it must not linger. */
const LEGACY_KEYS = ['auth_token', 'auth_user', 'auth_method'];

type Listener = () => void;
const listeners = new Set<Listener>();
let session: AuthSession | null = readHint();

function readHint(): AuthSession | null {
  try {
    for (const store of [localStorage, sessionStorage]) for (const k of LEGACY_KEYS) store.removeItem(k);
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthSession;
    return parsed?.profile?.uid ? parsed : null;
  } catch {
    return null;
  }
}

function setSession(next: AuthSession | null) {
  const changed = sessionKey(next) !== sessionKey(session) || next?.canUseStudio !== session?.canUseStudio;
  session = next;
  try {
    if (next) localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
    else localStorage.removeItem(PROFILE_KEY);
  } catch {
    /* storage unavailable: the hint lives in memory */
  }
  if (changed) listeners.forEach((l) => l());
}

export function getSession(): AuthSession | null {
  return session;
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function rememberPreference(): boolean {
  try {
    return localStorage.getItem(REMEMBER_KEY) !== '0';
  } catch {
    return true;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== PROFILE_KEY && e.key !== null) return;
    session = readHint();
    listeners.forEach((l) => l());
  });
}

/* ---------- account sheet visibility ---------- */

export type AccountView = 'sign-in' | 'register';

const sheetListeners = new Set<Listener>();
let sheetOpen = false;
let sheetView: AccountView = 'sign-in';

export function isSignInOpen(): boolean {
  return sheetOpen;
}

export function getAccountView(): AccountView {
  return sheetView;
}

export function subscribeSignIn(listener: Listener): () => void {
  sheetListeners.add(listener);
  return () => sheetListeners.delete(listener);
}

export function setSignInOpen(open: boolean, view: AccountView = 'sign-in') {
  if (sheetOpen === open && (!open || sheetView === view)) return;
  sheetOpen = open;
  if (open) sheetView = view;
  sheetListeners.forEach((l) => l());
}

/* ---------- API ---------- */

interface RawProfile {
  uid: string;
  provider: AuthMethod;
  name: string;
  email: string | null;
  username: string | null;
  title: string | null;
  customer_name: string | null;
  photo: string | null;
  can_use_studio: boolean;
}

interface RawSession {
  authenticated: boolean;
  profile: RawProfile | null;
}

function toSession(raw: RawSession | null | undefined): AuthSession | null {
  if (!raw?.authenticated || !raw.profile) return null;
  const p = raw.profile;
  return {
    method: p.provider,
    canUseStudio: p.can_use_studio === true,
    profile: {
      uid: p.uid,
      name: p.name || p.username || 'Ciright member',
      email: p.email,
      userName: p.username,
      title: p.title,
      customerName: p.customer_name,
      photo: p.photo && /^(https?:|data:image\/)/i.test(p.photo) ? p.photo : null,
    },
  };
}

const NETWORK_MESSAGE = 'Couldn’t reach Ciright. Check your connection and try again.';

async function call<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${AUTH_PATH}${path}`, {
      method,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(method === 'POST' ? { 'Content-Type': 'application/json', 'X-Requested-With': 'universe' } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new AuthError(NETWORK_MESSAGE, 'network');
  }
  let payload: { success?: boolean; data?: T; error?: string; message?: string; fields?: Record<string, string> } | null = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  if (!res.ok || !payload || payload.success === false || payload.data === undefined) {
    throw new AuthError(
      payload?.message || 'Sign-in is unavailable right now. Try again in a moment.',
      payload?.error || (res.status === 429 ? 'rate_limited' : 'unexpected'),
      payload?.fields
    );
  }
  return payload.data;
}

let statusRequest: Promise<AuthStatus> | null = null;

export function loadAuthStatus(): Promise<AuthStatus> {
  statusRequest ??= call<AuthStatus>('GET', '/status').catch((err) => {
    statusRequest = null;
    throw err;
  });
  return statusRequest;
}

type ExpiryListener = () => void;
const expiryListeners = new Set<ExpiryListener>();

export function onSessionExpired(listener: ExpiryListener): () => void {
  expiryListeners.add(listener);
  return () => expiryListeners.delete(listener);
}

/** The stored profile is only a hint; the cookie decides. */
export async function refreshSession(): Promise<AuthSession | null> {
  const hadSession = session !== null;
  try {
    const next = toSession(await call<RawSession>('GET', '/me'));
    setSession(next);
    if (hadSession && !next) expiryListeners.forEach((l) => l());
    return next;
  } catch {
    /* server unreachable: keep the hint; requests will be refused if it's stale */
    return session;
  }
}

export async function signIn(input: { username: string; password: string; remember: boolean }): Promise<AuthSession> {
  const next = toSession(
    await call<RawSession>('POST', '/ciright', { username: input.username.trim(), password: input.password, remember: input.remember })
  );
  if (!next) throw new AuthError('Sign-in is unavailable right now. Try again in a moment.', 'unexpected');
  try {
    localStorage.setItem(REMEMBER_KEY, input.remember ? '1' : '0');
  } catch {
    /* ignore */
  }
  setSession(next);
  return next;
}

export async function completeKeyraSignIn(result: { access_token?: string; verification_token?: string; userinfo?: unknown }): Promise<AuthSession> {
  const token = result.access_token || result.verification_token;
  if (!token) throw new AuthError('Keyra didn’t return a sign-in token.', 'token_missing');
  const next = toSession(await call<RawSession>('POST', '/keyra', { access_token: token, userinfo: result.userinfo ?? null }));
  if (!next) throw new AuthError('We couldn’t complete your Keyra sign-in.', 'unexpected');
  setSession(next);
  return next;
}

export async function devSignIn(): Promise<AuthSession> {
  const next = toSession(await call<RawSession>('POST', '/dev-bypass', {}));
  if (!next) throw new AuthError('Dev sign-in failed.', 'unexpected');
  setSession(next);
  return next;
}

export interface SignupInput {
  name: string;
  email: string;
  dialCode: string;
  number: string;
  password: string;
  termsAccepted: boolean;
}

export type SignupOutcome = { status: 'complete'; session: AuthSession } | { status: 'created_sign_in_required'; message: string };

export async function signUp(input: SignupInput): Promise<SignupOutcome> {
  const data = await call<{ status: 'complete' | 'created_sign_in_required'; message?: string } & Partial<RawSession>>('POST', '/register', {
    name: input.name.trim(),
    email: input.email.trim(),
    phone: { dial_code: input.dialCode, number: input.number },
    password: input.password,
    terms_accepted: input.termsAccepted,
  });
  if (data.status === 'complete') {
    const next = toSession({ authenticated: Boolean(data.authenticated), profile: data.profile ?? null });
    if (next) {
      setSession(next);
      return { status: 'complete', session: next };
    }
  }
  return { status: 'created_sign_in_required', message: data.message ?? 'Your account was created. Sign in to continue.' };
}

export async function signOut(): Promise<void> {
  setSession(null);
  await call('POST', '/logout', {}).catch(() => undefined);
}

/** Same-origin paths only: no scheme, no protocol-relative `//host`, no backslash tricks. */
export function safeReturnTo(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return null;
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null;
  } catch {
    return null;
  }
}
